/**
 * src/admin-ui/src/pages/ModelPricing.tsx
 *
 * Administrator model-pricing editor on the shared full-screen table
 * pattern (components/edge-table). Provider/model columns are
 * filterable (the catalog is loaded at once); price cells stay
 * editable inline, so they carry no filters. The "add model" action
 * and the pricing hint live in the bottom bar.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopyOutlined } from "@mui/icons-material";
import {
  ModelPrices,
  addModel,
  getModels,
  getUnitTypes,
  updateModelPrices,
} from "../services/pricing";
import EdgeDataTable, {
  EdgeColumn,
} from "../components/edge-table/EdgeDataTable";
import { BarTile } from "../components/edge-table/EdgeTableLayout.styles";
import { FilterableCell } from "../utils/usageTableFilters";
import { copyToClipboard } from "../utils/clipboard";
import {
  PriceFields,
  PriceInput,
  PricingCardHeader,
} from "./ModelPricing.styles";

type PriceDraft = Record<number, Record<string, string>>;

const PAGE_SIZE = 100;

function buildDraft(rows: ModelPrices[]): PriceDraft {
  return Object.fromEntries(
    rows.map((row) => [row.model_id, { ...row.prices }]),
  );
}

const ModelPricing: React.FC = () => {
  const [models, setModels] = useState<ModelPrices[]>([]);
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [draft, setDraft] = useState<PriceDraft>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProvider, setNewProvider] = useState("");
  const [newModelName, setNewModelName] = useState("");
  const [addingModel, setAddingModel] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [unitTypesData, modelsData] = await Promise.all([
        getUnitTypes(),
        getModels(),
      ]);
      setUnitTypes(unitTypesData);
      setModels(modelsData);
      setDraft(buildDraft(modelsData));
    } catch (error) {
      console.error("Failed to load pricing data", error);
      setToast({
        message: "Не удалось загрузить данные о ценах",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePriceChange = (
    modelId: number,
    unitType: string,
    value: string,
  ) => {
    setDraft((previous) => ({
      ...previous,
      [modelId]: { ...(previous[modelId] || {}), [unitType]: value },
    }));
  };

  const handleSaveRow = async (model: ModelPrices) => {
    const prices = Object.fromEntries(
      Object.entries(draft[model.model_id] || {}).filter(
        ([, value]) =>
          value !== undefined && value !== null && String(value).trim() !== "",
      ),
    );
    if (Object.keys(prices).length === 0) {
      setToast({ message: "Укажите хотя бы одну цену", severity: "error" });
      return;
    }

    setSavingId(model.model_id);
    try {
      const updated = await updateModelPrices(model.model_id, prices);
      setModels((previous) =>
        previous.map((item) =>
          item.model_id === updated.model_id ? updated : item,
        ),
      );
      setDraft((previous) => ({
        ...previous,
        [updated.model_id]: { ...updated.prices },
      }));
      setToast({
        message: `Цены сохранены: ${model.provider}/${model.model_name}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save prices", error);
      setToast({ message: "Не удалось сохранить цены", severity: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const handleAddModel = async () => {
    if (!newProvider.trim() || !newModelName.trim()) {
      setToast({
        message: "Заполните провайдера и имя модели",
        severity: "error",
      });
      return;
    }
    setAddingModel(true);
    try {
      const created = await addModel(newProvider.trim(), newModelName.trim());
      setModels((previous) =>
        previous.some((model) => model.model_id === created.model_id)
          ? previous
          : [...previous, created],
      );
      setDraft((previous) => ({
        ...previous,
        [created.model_id]: { ...created.prices },
      }));
      setDialogOpen(false);
      setNewProvider("");
      setNewModelName("");
      setToast({
        message: `Модель добавлена: ${created.provider}/${created.model_name}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to add model", error);
      setToast({ message: "Не удалось добавить модель", severity: "error" });
    } finally {
      setAddingModel(false);
    }
  };

  const sortedModels = useMemo(
    () =>
      [...models].sort((left, right) =>
        left.provider === right.provider
          ? left.model_name.localeCompare(right.model_name)
          : left.provider.localeCompare(right.provider),
      ),
    [models],
  );

  const renderPriceInput = (model: ModelPrices, unit: string) => (
    <PriceInput
      key={unit}
      variant="standard"
      type="number"
      size="small"
      label={unit}
      value={draft[model.model_id]?.[unit] ?? ""}
      onChange={(event) =>
        handlePriceChange(model.model_id, unit, event.target.value)
      }
      inputProps={{ step: "any", min: 0 }}
    />
  );

  const getCell = useCallback(
    (row: ModelPrices, columnId: string): FilterableCell => {
      switch (columnId) {
        case "provider":
          return { text: row.provider };
        case "model_name":
          return { text: row.model_name };
        default:
          return { text: draft[row.model_id]?.[columnId] ?? "" };
      }
    },
    [draft],
  );

  const columns = useMemo<EdgeColumn<ModelPrices>[]>(
    () => [
      { id: "provider", label: "Провайдер", filterKind: "text" },
      { id: "model_name", label: "Модель", filterKind: "text" },
      ...unitTypes.map((unit) => ({
        id: unit,
        label: unit,
        numeric: true,
        render: (model: ModelPrices) => renderPriceInput(model, unit),
      })),
      {
        id: "actions",
        label: "Действие",
        render: (model: ModelPrices) => (
          <Button
            variant="outlined"
            size="small"
            disabled={savingId === model.model_id}
            onClick={() => handleSaveRow(model)}
          >
            {savingId === model.model_id ? "..." : "Сохранить"}
          </Button>
        ),
      },
    ],
    // renderPriceInput/handleSaveRow are stable enough per render; the
    // memo only guards against unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitTypes, savingId, draft],
  );

  return (
    <>
      <EdgeDataTable
        ariaLabel="Редактор цен моделей"
        columns={columns}
        rows={sortedModels}
        getCell={getCell}
        rowKey={(row) => row.model_id}
        loading={loading}
        emptyMessage="Нет моделей. Добавьте первую модель."
        clientPageSize={PAGE_SIZE}
        renderMobileCard={(model) => (
          <>
            <PricingCardHeader>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {model.model_name}
                </Typography>
                <Tooltip title="Скопировать идентификатор">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const success = await copyToClipboard(model.model_name);
                      setToast({
                        message: success
                          ? "Скопировано в буфер обмена"
                          : "Ошибка копирования",
                        severity: success ? "success" : "error",
                      });
                    }}
                  >
                    <ContentCopyOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {model.provider}
              </Typography>
            </PricingCardHeader>
            <PriceFields>
              {unitTypes.map((unit) => renderPriceInput(model, unit))}
            </PriceFields>
            <Button
              variant="outlined"
              disabled={savingId === model.model_id}
              onClick={() => handleSaveRow(model)}
            >
              {savingId === model.model_id ? "Сохранение..." : "Сохранить"}
            </Button>
          </>
        )}
        bottomTiles={
          <>
            <BarTile
              sx={{
                display: { xs: "none", md: "inline-flex" },
                maxWidth: 420,
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                Цены в баллах за единицу; сохранение создаёт запись в истории
                цен
              </Typography>
            </BarTile>
            <BarTile>
              <Button
                variant="contained"
                size="small"
                onClick={() => setDialogOpen(true)}
              >
                Добавить модель
              </Button>
            </BarTile>
          </>
        }
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Добавить модель</DialogTitle>
        <DialogContent>
          <TextField
            label="Провайдер"
            fullWidth
            margin="normal"
            value={newProvider}
            onChange={(event) => setNewProvider(event.target.value)}
            placeholder="openai"
          />
          <TextField
            label="Имя модели"
            fullWidth
            margin="normal"
            value={newModelName}
            onChange={(event) => setNewModelName(event.target.value)}
            placeholder="gpt-4.1-nano"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleAddModel}
            disabled={addingModel}
          >
            {addingModel ? "Добавление..." : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ModelPricing;
